import React, {useCallback, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {saveLinkToken} from './tossBillingLinkResult';
import {loadTossPayments} from '@tosspayments/tosspayments-sdk';
import {AlertCircle, CreditCard, Loader2, ShieldCheck} from 'lucide-react';

import {getCardLinkInfo, verifyCardLink} from '../api/billing/cardLink.api';

/**
 * 고객이 **로그인 없이** 카드를 등록하는 고유 링크 화면. `/card/register?token=...`
 *
 * ## 왜 사업자등록번호를 묻나
 *
 * 링크만으로 카드를 붙일 수 있으면, 링크가 전달 과정에서 새는 순간 남이 그 계정에 카드를 붙인다.
 * 종전 링크(`/direct/payments?accountId=145`)는 **본인 확인이 아예 없었고** URL 의 숫자만 바꾸면
 * 다른 계정이 됐다(`_handover/11_RISK_REGISTER §DP` DP-04).
 *
 * ⇒ **추측 불가능한 토큰**(URL) + **사업자등록번호**(본인 확인) 두 개를 함께 요구한다.
 * 둘 중 하나만으로는 통과할 수 없다 — 사업자번호는 거래명세서 등에 적혀 있어 비밀이 아니고,
 * 토큰은 본인 확인이 아니기 때문이다. 서버가 **5회 오입력 시 링크를 잠근다**.
 *
 * ## 🔴 이 화면으로 할 수 있는 일은 카드 '등록' 하나뿐이다
 *
 * 카드 목록 조회·삭제는 없다. 링크를 손에 넣은 사람이 할 수 있는 최악이
 * "그 계정에 카드가 한 장 붙는 것" 으로 제한된다.
 *
 * ⚠️ **2026-09-10 에 범위가 한 칸 늘었다** — 등록 직전에 "이 카드를 기본 결제 카드로
 * 설정하시겠습니까?" 를 묻고, '네' 면 그 카드가 청구 대상이 된다(사용자 지시).
 * 🔑 2026-09-11 부터 그 질문은 **기본카드가 이미 있을 때만** 뜬다 — 없으면 어차피 이 카드가
 * 기본이 되므로 답이 정해진 질문이고, 고객에게 무의미한 선택을 시키지 않는다.
 * 기존 카드가 노출·삭제되지는 않고, 대상 계정은 티켓이 정하므로 **남의 계정에는 갈 수 없다.**
 * 판정 근거 = `TossPaymentsController.linkBillingKeyRegistration` KDoc.
 *
 * 설계: `_ai-runs/2026-09-07_002_install_wizard/011_round5_card_link.md`
 */

/**
 * 토스 빌링(카드등록) 클라이언트 키.
 *
 * ⚠️ 클라이언트 키는 브라우저에 노출되는 공개값이라 비밀이 아니다.
 * 🟡 다만 같은 값이 `CardRegistrationPopup.js` 에도 박혀 있다(총 2곳).
 *    키를 바꿀 일이 생기면 두 곳을 함께 고쳐야 한다 — 상수로 모으는 것은 별건으로 둔다
 *    (그쪽이 운영 중인 결제 화면이라 이번 변경 범위에서 건드리지 않는다).
 */
const TOSS_BILLING_CLIENT_KEY = 'live_ck_oeqRGgYO1r5q1QwOjmOrQnN2Eyaz';

/** 사업자등록번호 표시 형식 `123-45-67890`. 서버는 숫자만 비교하므로 화면 편의일 뿐이다. */
const formatBizNumber = (raw) => {
    const digits = String(raw ?? '').replace(/\D/g, '').slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

export default function CardRegisterByLink() {
    const [query] = useSearchParams();
    const token = query.get('token');

    const [isLoading, setIsLoading] = useState(true);
    const [storeName, setStoreName] = useState(null);
    /** 링크 자체가 무효일 때의 사유. 이 값이 있으면 입력 폼을 아예 보여주지 않는다. */
    const [linkError, setLinkError] = useState(null);

    const [bizRegNumber, setBizRegNumber] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    /**
     * 본인 확인을 통과해 받은 1회용 티켓. 값이 있으면 **기본카드 확인창**이 떠 있는 상태다.
     *
     * 🔑 확인창을 **본인 확인 뒤**에 띄운다. 앞에 두면 사업자번호를 틀린 고객이 답을 먼저 하게 되고,
     *    뒤에 두면 티켓(유효 15분)을 손에 든 채 답하므로 흐름이 한 번에 끝난다.
     */
    const [pendingTicket, setPendingTicket] = useState(null);

    useEffect(() => {
        if (!token) {
            setLinkError('링크가 올바르지 않습니다. 담당자에게 다시 요청해주세요.');
            setIsLoading(false);
            return;
        }

        getCardLinkInfo(token)
            .then((res) => {
                const data = res?.data;
                if (!data?.valid) {
                    setLinkError(data?.message ?? '링크가 올바르지 않습니다. 담당자에게 다시 요청해주세요.');
                    return;
                }
                setStoreName(data.storeName ?? null);
            })
            .catch(() => setLinkError('링크를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.'))
            .finally(() => setIsLoading(false));
    }, [token]);

    /**
     * 확인 통과 후 토스 카드등록 창을 연다.
     *
     * 🔑 성공 URL 에 **`ticket` 만** 싣는다. 계정 식별자를 싣지 않는 것이 이 설계의 핵심이다 —
     * 콜백 URL 은 결제창을 거치며 히스토리·리퍼러에 남는다.
     */
    const openTossBilling = useCallback((ticket, setDefault) => {
        const origin = window.location.origin;
        /*
          🔑 기본카드 여부를 **콜백 URL 에 싣는다.** 토스 결제창을 거치면 이 페이지의 state 는
             사라지므로(전체 페이지 이동), 답을 다음 화면까지 넘기는 길은 URL 뿐이다.
             서버에 저장하려면 `card_registration_link` 에 컬럼이 필요하다(DDL) — 위조로 얻을
             것이 없어서 쿼리로 둔다(근거 = 서버 KDoc).
        */
        const callback = `${origin}/payments/toss/billing/link`
            + `?ticket=${encodeURIComponent(ticket)}`
            + `&setDefault=${setDefault ? 'true' : 'false'}`;

        /*
          🔑 링크 토큰을 이 탭의 sessionStorage 에 남긴다(2026-10-23). 콜백 URL 에는 설계상 토큰이 없어서,
             티켓 만료·취소로 돌아온 고객이 "다시 진행" 할 길이 없었다(처음 받은 링크를 다시 찾아야 했다).
             sessionStorage 는 같은 탭 안에서만 살고 토스 결제창을 거쳐 돌아와도 남는다.
             토큰만으로는 카드를 붙일 수 없고(사업자번호 확인 필요) URL 에 싣는 것도 아니라 설계 원칙을 깨지 않는다.
        */
        saveLinkToken(token);

        return loadTossPayments(TOSS_BILLING_CLIENT_KEY).then((tossPayments) => {
            const payment = tossPayments.payment({customerKey: `${Date.now()}`});
            return payment.requestBillingAuth({
                method: 'CARD',
                successUrl: callback,
                failUrl: callback,
                customerName: storeName ?? '고객',
            });
        });
    }, [storeName, token]);

    /**
     * 확인창의 '네' / '아니오'. 둘 다 **같은 등록 흐름으로 이어진다** — 답만 다르다.
     *
     * 🔴 '아니오' 를 골라도 **기본카드가 하나도 없는 계정이면 이 카드가 기본이 된다**(서버 판정).
     *    그래서 확인창 문구에 "기존 카드가 없으면 이 카드가 기본이 됩니다" 를 적어 둔다 —
     *    적지 않으면 '아니오' 를 누른 고객이 나중에 "기본이 아니라고 했는데" 라고 묻게 된다.
     */
    const answerDefaultCard = (setDefault) => {
        const ticket = pendingTicket;
        setPendingTicket(null);

        openTossBilling(ticket, setDefault).catch(() => {
            setError('결제창을 열지 못했습니다. 잠시 후 다시 시도해주세요.');
            setIsSubmitting(false);
        });
    };

    const submit = () => {
        const digits = bizRegNumber.replace(/\D/g, '');
        if (digits.length !== 10) {
            setError('사업자등록번호 10자리를 입력해주세요.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        verifyCardLink(token, digits)
            .then((res) => {
                const data = res?.data;
                if (!data?.verified) {
                    // 남은 횟수를 함께 보여준다 — 몇 번 더 틀리면 잠기는지 알아야 담당자에게 연락한다.
                    const remain = data?.remainingAttempts;
                    setError(
                        remain != null
                            ? `${data?.message ?? '확인하지 못했습니다.'} (남은 입력 ${remain}회)`
                            : (data?.message ?? '확인하지 못했습니다.')
                    );
                    setIsSubmitting(false);
                    return;
                }

                /*
                  🔑 **기본카드가 이미 있을 때만 묻는다**(2026-09-11 사용자 지시).
                     없으면 이 카드가 어차피 기본이 되므로("카드는 있는데 청구할 카드가 없는"
                     상태를 만들지 않는다 — 서버 `DefaultCardPolicy` 불변식) 확인창은
                     **답이 정해진 질문**이다. 묻지 않고 바로 결제창을 연다.

                  🔴 `hasDefaultCard` 가 `undefined` 면(구 백엔드) **묻는 쪽**으로 간다.
                     모르는 상태에서 조용히 기본카드를 바꾸는 것보다, 한 번 더 묻는 편이 안전하다.
                */
                if (data.hasDefaultCard === false) {
                    openTossBilling(data.ticket, false).catch(() => {
                        setError('결제창을 열지 못했습니다. 잠시 후 다시 시도해주세요.');
                        setIsSubmitting(false);
                    });
                    return;
                }

                // 버튼은 잠긴 채로 둔다(다시 누르면 티켓이 두 번 발급된다).
                setPendingTicket(data.ticket);
            })
            .catch(() => {
                setError('확인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
                setIsSubmitting(false);
            });
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 py-12">
            <div className="w-full max-w-md bg-white rounded-3xl shadow-sm p-7">
                <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="w-5 h-5 text-blue-600"/>
                    <h1 className="text-[20px] font-bold text-gray-900">결제 카드 등록</h1>
                </div>
                <p className="text-[13px] text-gray-500 mb-6">오모플레이 서비스 이용을 위한 결제 카드를 등록합니다.</p>

                {isLoading ? (
                    <div className="flex items-center justify-center py-16 text-gray-400 gap-2 text-[14px]">
                        <Loader2 className="w-5 h-5 animate-spin"/>
                        확인 중입니다.
                    </div>
                ) : linkError ? (
                    <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5"/>
                        <p className="text-[13px] text-red-800 leading-relaxed">{linkError}</p>
                    </div>
                ) : (
                    <>
                        {storeName && (
                            <div className="mb-5 rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                                <span className="text-[12px] text-gray-500">업체명</span>
                                <div className="text-[15px] font-bold text-gray-900">{storeName}</div>
                            </div>
                        )}

                        <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                            사업자등록번호
                        </label>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoFocus
                            value={bizRegNumber}
                            onChange={(e) => setBizRegNumber(formatBizNumber(e.target.value))}
                            onKeyDown={(e) => e.key === 'Enter' && !isSubmitting && submit()}
                            placeholder="123-45-67890"
                            className="w-full px-4 py-3.5 bg-white border border-gray-300 rounded-xl text-[16px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all placeholder-gray-300"
                        />
                        <p className="mt-2 text-[12px] text-gray-400 leading-relaxed">
                            본인 확인을 위해 필요합니다. 등록하신 사업자등록번호와 일치해야 다음으로 넘어갑니다.
                        </p>

                        {error && (
                            <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-4 flex items-start gap-2">
                                <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5"/>
                                <p className="text-[13px] text-red-800 leading-relaxed">{error}</p>
                            </div>
                        )}

                        <button
                            onClick={submit}
                            disabled={isSubmitting}
                            className="mt-5 w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-xl py-4 font-semibold text-[16px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-colors"
                        >
                            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <ShieldCheck className="w-4 h-4"/>}
                            확인하고 카드 등록하기
                        </button>

                        {/*
                          이 링크로 할 수 있는 일의 범위를 고객에게 명시한다.
                          "링크 하나로 내 결제수단이 어디까지 노출되나" 는 당연한 우려이고,
                          실제로 추가만 가능하므로 그대로 적는다.
                        */}
                        <p className="mt-4 text-[11px] text-gray-400 leading-relaxed text-center">
                            이 화면에서는 카드 <strong className="text-gray-500">등록만</strong> 가능합니다.
                            등록된 카드 조회·삭제는 로그인 후 이용해주세요.
                        </p>
                    </>
                )}

                {/*
                  기본 결제 카드 확인창(2026-09-10 사용자 지시).

                  🔑 `window.confirm` 을 쓰지 않는다 — 브라우저 기본 확인창은 문구를 꾸밀 수 없어
                     "기존 카드가 없으면 어차피 기본이 된다" 는 단서를 읽히게 넣을 수 없고,
                     모바일에서 도메인 이름과 함께 떠 결제 흐름에서 이질적으로 보인다.

                  🔴 **닫기(x)나 바깥 클릭으로 없앨 수 없게** 두었다. 여기서 그냥 닫으면 티켓만
                     쓰고 카드가 등록되지 않은 채 끝나서, 고객은 등록했다고 생각하고 담당자는
                     못 받은 상태가 된다. 답은 둘 중 하나여야 한다.
                */}
                {pendingTicket && (
                    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                        <div className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl">
                            <h2 className="text-[17px] font-bold text-gray-900 leading-snug">
                                이 카드를<br/>기본 결제 카드로 설정하시겠습니까?
                            </h2>
                            <p className="mt-3 text-[13px] text-gray-600 leading-relaxed">
                                기본 결제 카드는 <strong className="text-gray-800">서비스 이용료가 청구되는 카드</strong>입니다.
                                <br/>
                                <span className="text-gray-500">
                                    ‘네’ 를 누르면 지금 등록하는 카드로 청구 카드가 바뀝니다.
                                </span>
                            </p>
                            {/*
                              🔑 이 확인창은 **기본 결제 카드가 이미 있을 때만** 뜬다(2026-09-11).
                                 그래서 종전의 "기존 카드가 없으면 어차피 기본이 됩니다" 단서는
                                 이 자리에서 더 이상 맞는 설명이 아니라 지웠다. 대신 '아니오' 가
                                 무엇을 뜻하는지 적는다 — 안 적으면 "그럼 이 카드는 왜 등록하나" 가 된다.
                            */}
                            <p className="mt-2 text-[12px] text-gray-400 leading-relaxed">
                                ‘아니오’ 를 누르면 카드는 등록되지만 청구 카드는 기존 카드로 그대로 유지됩니다.
                            </p>

                            <div className="mt-5 flex gap-2">
                                <button
                                    onClick={() => answerDefaultCard(false)}
                                    className="flex-1 py-3 rounded-xl border border-gray-300 bg-white text-gray-700 font-semibold text-[15px] hover:bg-gray-50 transition-colors"
                                >
                                    아니오
                                </button>
                                <button
                                    onClick={() => answerDefaultCard(true)}
                                    className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-semibold text-[15px] hover:bg-blue-700 transition-colors"
                                >
                                    네
                                </button>
                            </div>
                            <p className="mt-3 text-[11px] text-gray-400 text-center">
                                답을 고르면 카드 등록 창이 열립니다.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
